import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { Loading } from "@/components/common/Chrome";
import { LicenceGate } from "@/components/common/LicenceGate";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Approvals from "@/pages/Approvals";
import AuditExplorer from "@/pages/AuditExplorer";
import Beneficiaries from "@/pages/Beneficiaries";
import BoardPack from "@/pages/BoardPack";
import Budget from "@/pages/Budget";
import Claims from "@/pages/Claims";
import ClaimDetails from "@/pages/ClaimDetails";
import ComplianceDashboard from "@/pages/ComplianceDashboard";
import Conflicts from "@/pages/Conflicts";
import DecisionSimulator from "@/pages/DecisionSimulator";
import Donations from "@/pages/Donations";
import DonorOnboarding from "@/pages/DonorOnboarding";
import DonorPaymentReturn from "@/pages/DonorPaymentReturn";
import DonorPortal from "@/pages/DonorPortal";
import DonorSignin from "@/pages/DonorSignin";
import Donors from "@/pages/Donors";
import EvidenceRegistry from "@/pages/EvidenceRegistry";
import Exceptions from "@/pages/Exceptions";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import Finance from "@/pages/Finance";
import ExpenseClaims from "@/pages/ExpenseClaims";
import Fundraising from "@/pages/Fundraising";
import Funds from "@/pages/Funds";
import GiveThanks from "@/pages/GiveThanks";
import Governance from "@/pages/Governance";
import Grants from "@/pages/Grants";
import HallHire from "@/pages/HallHire";
import HireAgreementSign from "@/pages/HireAgreementSign";
import HelpGuide from "@/pages/HelpGuide";
import HrPayroll from "@/pages/HrPayroll";
import ImpactReport from "@/pages/ImpactReport";
import InquiriesPage from "@/pages/InquiriesPage";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import OnboardingQueue from "@/pages/OnboardingQueue";
import OrgSetup from "@/pages/OrgSetup";
import Programs from "@/pages/Programs";
import PublicGive from "@/pages/PublicGive";
import Privacy from "@/pages/Privacy";
import Payments from "@/pages/Payments";
import Property from "@/pages/Property";
import RiskAml from "@/pages/RiskAml";
import RuleDetail from "@/pages/RuleDetail";
import RuleWorkbench from "@/pages/RuleWorkbench";
import Rbac from "@/pages/Rbac";
import Subscription from "@/pages/Subscription";
import SupplyChain from "@/pages/SupplyChain";
import Volunteers from "@/pages/Volunteers";
import Apprenticeships from "@/pages/Apprenticeships";
import MyLeave from "@/pages/MyLeave";
import Cafeteria from "@/pages/Cafeteria";
import ImpactDashboard from "@/pages/ImpactDashboard";
import Meetings from "@/pages/Meetings";
import ExecutiveCommittee from "@/pages/ExecutiveCommittee";
import Elections from "@/pages/Elections";
import VotePage from "@/pages/VotePage";
import PublicResults from "@/pages/PublicResults";
import PublicAnnualReport from "@/pages/PublicAnnualReport";
import ApprenticePortal, { ApprenticeSignin } from "@/pages/ApprenticePortal";
import VolunteerHub from "@/pages/VolunteerHub";
import VolunteerApply from "@/pages/VolunteerApply";
import VolunteerSignin from "@/pages/VolunteerSignin";
import VolunteerPortal from "@/pages/VolunteerPortal";
import PublicWall from "@/pages/PublicWall";
import Recognition from "@/pages/Recognition";
import TimeSheet from "@/pages/TimeSheet";
import WorksDashboard from "@/pages/WorksDashboard";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Loading label="Establishing secure session" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <LicenceGate>{(blocked) => <AppShell licenceBlocked={blocked}>{children}</AppShell>}</LicenceGate>;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center"><Loading /></div>;
  if (user) return <Navigate to="/dashboard/executive" replace />;
  return children;
};

const ROUTES = [
  ["/dashboard/executive", <ExecutiveDashboard />],
  ["/compliance/overview", <ComplianceDashboard />],
  ["/regulatory/rules", <RuleWorkbench />],
  ["/regulatory/rules/:ruleId", <RuleDetail />],
  ["/regulatory/exceptions", <Exceptions />],
  ["/regulatory/evidence", <EvidenceRegistry />],
  ["/regulatory/simulator", <DecisionSimulator />],
  ["/platform/audit", <AuditExplorer />],
  ["/charity/donors", <Donors />],
  ["/charity/donations", <Donations />],
  ["/charity/fundraising", <Fundraising />],
  ["/charity/payments", <Payments />],
  ["/charity/onboarding", <OnboardingQueue />],
  ["/charity/funds", <Funds />],
  ["/charity/finance", <Finance />],
  ["/charity/grants", <Grants />],
  ["/charity/programs", <Programs />],
  ["/charity/impact-report", <ImpactReport />],
  ["/charity/beneficiaries", <Beneficiaries />],
  ["/charity/governance", <Governance />],
  ["/charity/governance/pack/:meetingId", <BoardPack />],
  ["/charity/budget", <Budget />],
  ["/charity/claims", <Claims />],
  ["/charity/expense-claims", <ExpenseClaims />],
  ["/governance/conflicts", <Conflicts />],
  ["/governance/inquiries", <InquiriesPage />],
  ["/governance/approvals", <Approvals />],
  ["/operations/hr", <HrPayroll />],
  ["/operations/works", <WorksDashboard />],
  ["/operations/volunteers", <Volunteers />],
  ["/operations/volunteer-hub", <VolunteerHub />],
  ["/operations/apprenticeships", <Apprenticeships />],
  ["/operations/cafeteria", <Cafeteria />],
  ["/dashboard/impact", <ImpactDashboard />],
  ["/governance/meetings", <Meetings />],
  ["/governance/executive-committee", <ExecutiveCommittee />],
  ["/governance/elections", <Elections />],
  ["/governance/vote", <VotePage />],
  ["/me/leave", <MyLeave />],
  ["/operations/time", <TimeSheet />],
  ["/operations/recognition", <Recognition />],
  ["/operations/supply", <SupplyChain />],
  ["/operations/property", <Property />],
  ["/compliance/privacy", <Privacy />],
  ["/compliance/risk", <RiskAml />],
  ["/settings/organization", <OrgSetup />],  ["/settings/rbac", <Rbac />],
  ["/help", <HelpGuide />],
  ["/settings/subscription", <Subscription />],
];

const Shell = () => {
  const { theme } = useTheme();
  useEffect(() => {
    document.title = "ServeCOR · Charity Management & Regulatory Intelligence Platform";
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" theme={theme} richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/claim-details/:token" element={<ClaimDetails />} />
          <Route path="/onboard/:token" element={<DonorOnboarding />} />          <Route path="/donor/signin" element={<DonorSignin />} />
          <Route path="/donor" element={<DonorPortal />} />
          <Route path="/donor/payment/return" element={<DonorPaymentReturn />} />
          <Route path="/give" element={<PublicGive />} />
          <Route path="/hall-hire" element={<HallHire />} />
          <Route path="/hire-agreement/:token" element={<HireAgreementSign />} />
          <Route path="/volunteer-apply/:token" element={<VolunteerApply />} />
          <Route path="/volunteer/signin" element={<VolunteerSignin />} />
          <Route path="/volunteer" element={<VolunteerPortal />} />
          <Route path="/apprentice/signin" element={<ApprenticeSignin />} />
          <Route path="/apprentice" element={<ApprenticePortal />} />
          <Route path="/walls/:wall" element={<PublicWall />} />
          <Route path="/results/:electionRef" element={<PublicResults />} />
          <Route path="/annual-report/:reportRef" element={<PublicAnnualReport />} />
          <Route path="/give/thanks" element={<GiveThanks />} />
          <Route path="/give/:campaign" element={<PublicGive />} />
          {ROUTES.map(([path, element]) => (
            <Route key={path} path={path} element={<Protected>{element}</Protected>} />
          ))}
          <Route path="/" element={<Navigate to="/dashboard/executive" replace />} />
          <Route path="*" element={<Protected><NotFound /></Protected>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <Shell />
    </ThemeProvider>
  );
}
